/**
 * Sagas for tethering (i.e. previewing questions from Question
 * Manager 2)
 */

import {
    call,
    put,
    select,
    fork,
    take,
    cancel,
    actionChannel,
    spawn,
    race,
} from 'redux-saga/effects';
import { buffers } from 'redux-saga';
import { fromJS } from 'immutable';
import log from 'loglevel';

import { wCall, throttledTakeEvery, asyncSleep } from 'phoenix2/utils';
import {
    REQUEST_START_TETHERING,
    STOP_TETHERING,
    REQUEST_STOP_TETHERING,
    TETHER_QUESTION,
    TETHER_VIDEO,
    startTethering,
    stopTethering,
    requestGetTetheringQuestion,
    REQUEST_GET_TETHERING_QUESTION,
    setTetheringCheckingMode,
} from './actions';
import { showAlert } from 'phoenix2/state/alert/sagas';
import { addConfigParameters } from 'phoenix2/state/config-params/actions';
import { request } from '../remote/sagas';
import { selectCurrentViewType } from 'phoenix2/state/views/selectors';
import { openSkillDeliveryView } from 'phoenix2/state/views/sagas';
import { setRootView, POP_VIEW } from 'phoenix2/state/views/actions';
import { RESULT_PAGE_CONTINUE } from 'phoenix2/state/question-activity/actions';
import {
    getLastTetherID,
    getTetheringSessionName,
    getVideo,
    isTetheringCheckingMode,
} from './selectors';
import { showVideo } from 'phoenix2/state/video/actions';
import { isAlertShowing } from 'phoenix2/state/alert/selectors';
import { END_ALERT, endAlert } from 'phoenix2/state/alert/actions';

// Key for storing the tethering session name in local storage user prefs:
const USER_PREF_KEY = 'tetherSessionName';

// Action types for tethering actions, that must be completed after switching
// into tethering mode
const TETHERING_CONTROL_ACTION_TYPES = [
    TETHER_QUESTION,
    TETHER_VIDEO,
    REQUEST_STOP_TETHERING,
    RESULT_PAGE_CONTINUE,
    POP_VIEW,
    END_ALERT,
];

/**
 * Saga to watch for startTethering requests and action them
 */
export function* watchRequestStartTethering() {
    yield throttledTakeEvery(REQUEST_START_TETHERING, doRequestStartTethering, 1);
}

/**
 * Saga to initiate tethering
 * @param action a requestStartTethering action with a sessionName and a
 * boolean ethermining whethere the session name should be stored in browser
 * local storage for next time
 */
function* doRequestStartTethering(action) {
    const sessionName = action.sessionName;

    // If the session name is not valid, show an alert and abort
    if (!typeof sessionName === 'string' || sessionName.length === 0) {
        try {
            yield call(showAlert, {
                title: 'Tethering failed',
                message: 'Please enter a valid name for your tethering session',
            });
        } catch (exc) {
            // AbortCurrentTasks exception should never be thrown as it is
            // only used in lessons mode, but want would want to just return
        }
        return;
    }

    // Save the session name in local storage for next time, if required:
    if (action.saveSessionName) {
        yield put(addConfigParameters(fromJS({ [USER_PREF_KEY]: sessionName }), true));
    }

    // Create an action channel to queue all incoming tethering requests (for
    // tethering a question, tethering a video, requesting to stop tethering and
    // tethering session ended by server). These actions could arrive before
    // we have switched to the tethering view, but we should only respond after)
    const tetheringChannel = yield actionChannel(
        TETHERING_CONTROL_ACTION_TYPES,
        buffers.expanding(),
    );

    // Update state with the fact that tethering has started
    yield put(startTethering(sessionName, 'fake'));

    // Try to initiate tethering with the server:
    yield put(requestGetTetheringQuestion());
    log.debug('Entering tethering mode');

    // Show the tethering view
    yield call(doShowTetheringView);

    // Start watching for questions and videos to be tethered, and for
    // the user and server stopping the tethering:
    const watchSaga = yield fork(watchTetheringControlActions, tetheringChannel);

    // Watch for tethering ending:
    yield take(STOP_TETHERING);

    // When ended, go back to the main tethering login page:
    yield put(setRootView({ type: 'mainmenu' }));

    // Close the channel:
    // tetheringChannel.flush();
    tetheringChannel.close();

    // When ended, cancel all the tethering-related watchers:
    yield cancel(watchSaga);
}

export function* watchRequestGetTetheringQuestion() {
    // Wait interval to clear out multiple tethered question watchers
    yield throttledTakeEvery(REQUEST_GET_TETHERING_QUESTION, requestTetheringQuestion, 0.5);
}

function* requestTetheringQuestion(action) {
    const { lastTetherID, requestChangeNotification } = action;
    const sessionName = yield select(getTetheringSessionName);

    let nextTetherID = lastTetherID;
    try {
        const resp = yield call(
            request,
            'startTethering',
            sessionName,
            lastTetherID,
            requestChangeNotification,
        );
        nextTetherID = resp.get('tetherID');
    } catch (exc) {
        log.debug('FAILED START TETHERING', exc);

        try {
            yield call(showAlert, {
                title: 'Tethering failed',
                message: 'An unexpected issue occurred.',
            });
        } catch (exc) {
            // AbortCurrentTasks exception should never be thrown as it is
            // only used in lessons mode, but want would want to just return
        }
        return;
    }

    // Spawn the timeout so that we can process another tethering question
    // if one comes in.
    yield spawn(waitForNextTethering, nextTetherID);
}

function* waitForNextTethering(nextTetherID) {
    const { timeout } = yield race({
        tetheredQuestion: take(REQUEST_GET_TETHERING_QUESTION),
        timeout: asyncSleep(5 * 60), // 5 minutes
    });
    if (timeout) {
        yield put(requestGetTetheringQuestion(nextTetherID));
    }
}

/**
 * Saga to show the tethering view
 */
function* doShowTetheringView() {
    yield put(setRootView({ type: 'tether' }));
}

/**
 * Saga to respond to queued tethering control actions by invoking other sagas
 */
function* watchTetheringControlActions(channel) {
    let questionFinished = false;

    while (true) {
        const action = yield take(channel);

        switch (action.type) {
            case TETHER_QUESTION:
                questionFinished = false;
                yield fork(doTetherQuestion, action);
                break;
            case TETHER_VIDEO:
                questionFinished = false;
                yield fork(doTetherVideo, action);
                break;
            case REQUEST_STOP_TETHERING:
                questionFinished = false;
                yield fork(doRequestStopTethering, action);
                break;
            case RESULT_PAGE_CONTINUE:
                questionFinished = true;
                break;
            case POP_VIEW:
                // Try to retether the last tethered question:
                yield fork(restartQuestion, questionFinished);
                questionFinished = false;
                break;
            case END_ALERT:
                // If alert is closed, give the alert time to close then
                // restart video (will only do something if it was the video
                // alert)
                yield asyncSleep(1);
                yield fork(restartVideo);
                break;
            default:
                break;
        }
    }
}

/**
 * Saga to show a question when tethered
 * @param action
 */
function* doTetherQuestion(action) {
    const questionData = fromJS(action.question);
    const lastTetherID = yield select(getLastTetherID);
    if (lastTetherID !== questionData.get('tetherID')) {
        // This is a new tether, so reset the checking mode
        yield put(setTetheringCheckingMode(false));
    }

    const checkingMode = yield select(isTetheringCheckingMode);

    // If showing an alert (e.g. tethering a video), close it:
    const alertShowing = yield select(isAlertShowing);

    if (alertShowing) {
        yield put(endAlert());
    }

    // Find out if we are on the tethering page - if so, push the skill
    // delivery view so we can go back to it, otherwise replace the view:
    const currentView = yield select(selectCurrentViewType);

    // If already showing a question, animate it out and then wait a short while
    // to animate in the new question to show that it is a new question:
    if (currentView === 'skilldelivery') {
        yield call(doShowTetheringView);
        yield asyncSleep(1);
    }

    // Create a skill delivery view for the question
    const question = fromJS({
        question_activity_id:
            questionData.get('activityIndex') + '/' + questionData.get('questionIndex'),
        activityIndex: questionData.get('activityIndex'),
        skill_id: questionData.get('skillID', ''),
        full_name: questionData.get('title', 'Tethered Question'),
        name: questionData.get('title', 'Tethered Question'),
        item_name: questionData.get('questionID', ''),
        pc: { steps: JSON.parse(questionData.get('questionSpec')) },
        task_title: 'Tethered task',
        package_title: 'Tethered package',
        bookwork_code: '---',
        item_position: 0,
    });

    // Either push or replace in the skill delivery view for the question:
    // Penultimate parameter tells openSkillDelivery not to inform the server
    // when the question is finished (only needed for students answering
    // questions)
    yield call(openSkillDeliveryView, question, null, 'push', false, checkingMode);
}

/**
 * Saga to show a video in the video alert when tethered
 * @param action - contains video which is an Immutable map containing
 *                  assetUrl: the base URL to fetch assets from
 *                  video_id: the UUID of the video
 */
function* doTetherVideo(action) {
    // If showing an alert (e.g. tethering a previous video), close it:
    const alertShowing = yield select(isAlertShowing);

    if (alertShowing) {
        yield put(endAlert());

        // Give previous video time to animate out:
        yield asyncSleep(1);
    }

    const currentView = yield select(selectCurrentViewType);

    // If not on the tethering view (e.g. showing a question), reset the root
    // view to the tethering view:
    if (currentView !== 'tether') {
        yield call(doShowTetheringView);
    }

    // Show the video in an alert - (pass current view name so the Google
    // Analytics pageView events are correct):
    const video = fromJS(action.video);
    yield put(
        showVideo(
            video.get('videoID'),
            '', // action.video.get('skill_id'),
            0,
            currentView,
            true,
            // corsSafe means we send our credentials while tethering
            true,
        ),
    );
}

/**
 * Saga that implements a request to stop tethering, by notifying the
 * server, and then taking the app out of tethering mode
 */
function* doRequestStopTethering() {
    // Tell the server to stop tethering:
    try {
        yield wCall(request, 'stopTethering');
    } catch (err) {
        log.debug('FAILED STOP TETHERING', err);
    }

    yield put(stopTethering());
}

/**
 * Restarts a tethering question (after it has been answered or the view was
 * popped), if a question has been tethered last
 */
function* restartQuestion(answered) {
    yield put(setTetheringCheckingMode(answered));
    yield put(requestGetTetheringQuestion('', false));
}

/**
 * Restarts a tethering video (after the view was popped), if a video has been
 * tethered last
 */
function* restartVideo() {
    const video = yield select(getVideo);
    if (video) {
        yield call(doTetherVideo, { video });
    }
}

/**
 * After a reconnection, restarts tethering using the given tethering
 * session name
 * @param sessionName
 */
export function* restartTethering(sessionName) {
    // Try to initiate tethering with the server:
    try {
        yield wCall(request, 'startTethering', sessionName);
    } catch (exc) {
        log.debug('FAILED RESTART TETHERING', exc);

        try {
            yield call(showAlert, {
                title: 'Restart tethering failed',
                message: 'An unexpected issue occurred.',
            });
        } catch (exc) {
            // AbortCurrentTasks exception should never be thrown as it is
            // only used in lessons mode, but want would want to just return
        }
        return;
    }
    yield put(startTethering(sessionName));
}
